import React, { useState, KeyboardEvent, FocusEvent, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Mail } from 'lucide-react';
import { RoleSelect } from '../common/RoleSelect';
import { OrgRoleSelect } from '../common/OrgRoleSelect';
import { BrandSelector } from '../common/BrandSelector';
import type { Brand } from '../../types';

interface MemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { emails?: string[]; brandId?: string; brandIds?: string[]; role: string }) => void;
  brands: Brand[];
  mode: 'add' | 'modify';
  initialData?: {
    email?: string;
    brandIds?: string[];
    role?: string;
  };
}

// Simple email validation function (adjust regex as needed for stricter validation)
const isValidEmail = (email: string): boolean => {
  // Basic regex, consider a more robust one if necessary
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const MemberModal: React.FC<MemberModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  brands,
  mode,
  initialData
}) => {
  const { t } = useTranslation();
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState<string[]>(initialData?.email ? [initialData.email] : []);
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>(initialData?.brandIds || []);
  const [selectedRole, setSelectedRole] = useState(initialData?.role || 'writer');
  const [selectedOrgRole, setSelectedOrgRole] = useState<'owner' | 'billing_admin' | 'admin' | 'member' | 'guest'>('member');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Sync internal state with incoming props when the modal opens
  // Reason: ensure the member's already-invited brand(s) and role are preselected in modify mode
  useEffect(() => {
    if (!isOpen) return;
    setSelectedBrandIds(initialData?.brandIds || []);
    setSelectedRole(initialData?.role || 'writer');
    setEmails(initialData?.email ? [initialData.email] : []);
    setSelectedOrgRole('member');
    setShowAdvanced(false);
    setEmailInput('');
  }, [isOpen, initialData]);

  const isModifyChanged = useMemo(() => {
    if (mode !== 'modify') return true; // In add mode, standard validation applies
    const originalBrandIds = (initialData?.brandIds || []).slice().sort();
    const currentBrandIds = selectedBrandIds.slice().sort();
    const brandsChanged = originalBrandIds.length !== currentBrandIds.length ||
      originalBrandIds.some((id, idx) => id !== currentBrandIds[idx]);
    const roleChanged = (initialData?.role || 'writer') !== selectedRole;
    const orgRoleChanged = selectedOrgRole !== 'member';
    return brandsChanged || roleChanged || orgRoleChanged;
  }, [mode, initialData, selectedBrandIds, selectedRole, selectedOrgRole]);

  if (!isOpen) return null;

  // Function to attempt adding an email to the list
  const attemptAddEmail = (emailToAdd: string) => {
    if (mode === 'modify') return; // Don't add emails in modify mode
    
    const trimmedEmail = emailToAdd.trim();
    if (trimmedEmail && isValidEmail(trimmedEmail) && !emails.includes(trimmedEmail)) {
      setEmails([...emails, trimmedEmail]);
      setEmailInput(''); // Clear input after successful add
    } else if (trimmedEmail && !isValidEmail(trimmedEmail)) {
       // Optional: Provide feedback for invalid email format
       console.warn("Invalid email format:", trimmedEmail);
       // You could set an error state here to show a message to the user
    } else if (trimmedEmail && emails.includes(trimmedEmail)) {
      // Optional: Provide feedback for duplicate email
      console.warn("Duplicate email:", trimmedEmail);
      // Clear the input even if it's a duplicate?
      // setEmailInput(''); 
    }
  };

  // Handle Enter/Comma key presses
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (mode === 'modify') return;
    
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault(); // Prevent form submission or comma input
      attemptAddEmail(emailInput);
    } else if (e.key === 'Backspace' && !emailInput && emails.length > 0) {
      // Keep Backspace logic to remove last added email
      setEmails(emails.slice(0, -1));
    }
  };

  // Handle losing focus (blur)
  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
     if (mode === 'modify') return;
     attemptAddEmail(e.target.value); // Attempt to add the current input value on blur
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    if (mode === 'modify') return;
    setEmails(emails.filter(email => email !== emailToRemove));
  };

  const handleSubmit = () => {
    // Accept the currently typed email if valid, even if Enter/comma wasn't pressed
    const trimmed = emailInput.trim();
    const typedIsValid = mode === 'add' && trimmed && isValidEmail(trimmed) && !emails.includes(trimmed);
    const effectiveEmails = typedIsValid ? [...emails, trimmed] : emails;

    const isValidAdd = mode === 'add' && effectiveEmails.length > 0 && selectedBrandIds.length === 1 && selectedRole;
    const isValidModify = mode === 'modify' && selectedBrandIds.length > 0 && selectedRole;

    if (isValidAdd || isValidModify) {
      const submitData = {
        ...(mode === 'add' && { emails: effectiveEmails }),
        ...(mode === 'add' ? { brandId: selectedBrandIds[0] } : { brandIds: selectedBrandIds }),
        role: selectedRole,
        orgRole: selectedOrgRole
      };
      onSubmit(submitData);
      onClose();
    } else {
      console.warn("Submit validation failed", { emails, selectedBrandIds, selectedRole, mode });
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl border border-black shadow-xl max-w-2xl w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">
              {mode === 'add' ? t('teamMembers.addMember') : (
                <span>
                  {t('teamMembers.modifyAccesses')}{' '}
                  <span className="italic text-gray-600">{initialData?.email}</span>
                </span>
              )}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-6">
            {mode === 'add' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('teamMembers.emails')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    placeholder={t('teamMembers.emailsPlaceholder')}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  {t('teamMembers.emailsHint')}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {emails.map((email, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-50 text-primary-700"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(email)}
                        className="ml-2 text-primary-600 hover:text-primary-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <BrandSelector
              brands={brands}
              selectedBrandIds={selectedBrandIds}
              onBrandSelect={setSelectedBrandIds}
              singleSelection={mode === 'add'}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('teamMembers.selectRole')}
              </label>
              <RoleSelect
                value={selectedRole}
                onChange={setSelectedRole}
              />
            </div>

            <div className="border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                {showAdvanced ? '▼' : '▶'} {t('teamMembers.advancedSettings.title', 'Advanced settings')}
              </button>
              {showAdvanced && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs text-gray-500">
                    {t('teamMembers.advancedSettings.orgRoleHint', 'Choose the role you want to give to this new user. By default, he will be a member')}
                  </p>
                  <OrgRoleSelect value={selectedOrgRole} onChange={setSelectedOrgRole} />
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={mode === 'add' 
                ? (emails.length === 0 || selectedBrandIds.length !== 1 || !selectedRole)
                : (!isModifyChanged || selectedBrandIds.length === 0 || !selectedRole)
              }
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mode === 'add' ? t('teamMembers.invite') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};